// self-reflection-data.js - pure helpers for the player post-match
// self-reflection (Module: reflection, a STANDALONE developmental lens - it
// NEVER feeds the Dragons Score, director decision 2026-07-19).
//
// Three age-appropriate question sets, chosen by the team's age band. Content
// is bilingual (English + Dutch) inline: the app renders in CONFIG.i18n.lang
// and falls back to English, exactly like strings.js. No DOM, no network -
// Vitest covers these directly.
//
// Question `type`:
//   emoji  - pick one feeling face (options carry the emoji + label)
//   choice - pick one labelled option (e.g. Yes / A bit / Not really)
//   stars  - 1..5 "how hard did you try" (rendered as stars)
//   scale  - 1..5 self-rating (rendered as a number scale)
//   text   - short free text (required)
//   textopt- short free text (optional)
// An answer value is: an option `value` string (emoji/choice), a 1..5 number
// (stars/scale), or a string (text/textopt).

// Shared option sets -----------------------------------------------------------

const FEELING_OPTIONS = [
  { value: 'great', emoji: '😀', en: 'Great', nl: 'Geweldig' },
  { value: 'good',  emoji: '🙂', en: 'Good',  nl: 'Goed' },
  { value: 'ok',    emoji: '😐', en: 'OK',    nl: 'Oke' },
  { value: 'tough', emoji: '🙁', en: 'Tough', nl: 'Zwaar' },
];

const FUN_OPTIONS = [
  { value: 'yes', en: 'Yes',        nl: 'Ja' },
  { value: 'bit', en: 'A bit',      nl: 'Een beetje' },
  { value: 'no',  en: 'Not really', nl: 'Niet echt' },
];

// The three bands ------------------------------------------------------------

export const REFLECTION_BANDS = {
  // Youngest (approx U11-U12) - a parent can help. Feelings, effort, fun.
  young: {
    key: 'young',
    titleEn: 'How was your game?',
    titleNl: 'Hoe was je wedstrijd?',
    questions: [
      { key: 'feel',     type: 'emoji',  options: FEELING_OPTIONS,
        en: 'How did you feel about the game?', nl: 'Hoe voelde je je over de wedstrijd?' },
      { key: 'effort',   type: 'stars',
        en: 'How hard did you try?', nl: 'Hoe hard heb je geprobeerd?' },
      { key: 'did_well', type: 'textopt',
        en: 'One thing you did well', nl: 'Een ding dat je goed deed' },
      { key: 'improve',  type: 'textopt',
        en: 'One thing to get better at', nl: 'Een ding om beter in te worden' },
      { key: 'fun',      type: 'choice', options: FUN_OPTIONS,
        en: 'Did you have fun?', nl: 'Had je plezier?' },
    ],
  },

  // Middle (approx U14) - what went well / even better if.
  middle: {
    key: 'middle',
    titleEn: 'Reflect on your game',
    titleNl: 'Blik terug op je wedstrijd',
    questions: [
      { key: 'effort',      type: 'scale',
        en: 'Effort - how hard did you work?', nl: 'Inzet - hoe hard heb je gewerkt?' },
      { key: 'went_well',   type: 'text',
        en: 'What went well for you today?', nl: 'Wat ging er vandaag goed bij jou?' },
      { key: 'even_better', type: 'text',
        en: 'Even better if... what would you change?', nl: 'Nog beter als... wat zou je anders doen?' },
      { key: 'helped_team', type: 'textopt',
        en: 'How did you help the team?', nl: 'Hoe heb je het team geholpen?' },
      { key: 'work_on',     type: 'text',
        en: 'One thing to work on before the next game', nl: 'Een ding om aan te werken voor de volgende wedstrijd' },
      { key: 'feel',        type: 'emoji', options: FEELING_OPTIONS,
        en: 'How did you feel out there?', nl: 'Hoe voelde je je op het veld?' },
    ],
  },

  // Oldest (approx U16-U19) - performance review + goals.
  older: {
    key: 'older',
    titleEn: 'Post-match self-review',
    titleNl: 'Zelfevaluatie na de wedstrijd',
    questions: [
      { key: 'performance', type: 'scale',
        en: 'Overall performance - rate yourself', nl: 'Algemene prestatie - beoordeel jezelf' },
      { key: 'work_rate',   type: 'scale',
        en: 'Effort & work-rate', nl: 'Inzet & werktempo' },
      { key: 'happy_with',  type: 'text',
        en: 'What were you happy with in your game?', nl: 'Waar was je tevreden over in je spel?' },
      { key: 'struggled',   type: 'text',
        en: 'Where did you struggle, or what would you change?', nl: 'Waar had je het moeilijk, of wat zou je veranderen?' },
      { key: 'mental',      type: 'scale',
        en: 'Mentally - focus & confidence', nl: 'Mentaal - focus & vertrouwen' },
      { key: 'next_goal',   type: 'text',
        en: 'Your goal for the next game', nl: 'Je doel voor de volgende wedstrijd' },
      { key: 'coach_note',  type: 'textopt',
        en: 'Anything you want your coach to know?', nl: 'Iets dat je je coach wilt laten weten?' },
    ],
  },
};

export const BANDS = ['young', 'middle', 'older'];
const OPTIONAL_TYPES = new Set(['textopt']);

/** Largest U-number in an age_group label ("U11-U12" -> 12, "U14" -> 14). */
function _maxAge(ageGroup) {
  const nums = String(ageGroup || '').match(/\d+/g);
  if (!nums) return null;
  return Math.max(...nums.map(Number));
}

/**
 * Map a team age_group to a reflection band. The club stores three buckets
 * (U11-U12 / U14 / U16-U19) but this bands by number so any label works:
 * <=12 young, 13-15 middle, >=16 older. Defaults to 'middle' when unknown.
 */
export function bandForAgeGroup(ageGroup) {
  const n = _maxAge(ageGroup);
  if (n == null) return 'middle';
  if (n <= 12) return 'young';
  if (n <= 15) return 'middle';
  return 'older';
}

/** The band definition, or null for an unknown key. */
export function bandDef(band) {
  return REFLECTION_BANDS[band] || null;
}

/** Localised band title. */
export function bandTitle(band, lang = 'en') {
  const b = REFLECTION_BANDS[band];
  if (!b) return '';
  return (lang === 'nl' && b.titleNl) ? b.titleNl : b.titleEn;
}

/** The questions for a band (empty array for an unknown band). */
export function questionsForBand(band) {
  return REFLECTION_BANDS[band]?.questions || [];
}

/** Localised question prompt. */
export function questionLabel(q, lang = 'en') {
  if (!q) return '';
  return (lang === 'nl' && q.nl) ? q.nl : q.en;
}

/** Localised label for one of a question's options. */
export function optionLabel(q, value, lang = 'en') {
  const opt = (q?.options || []).find(o => o.value === value);
  if (!opt) return '';
  return (lang === 'nl' && opt.nl) ? opt.nl : opt.en;
}

/** Keys the player MUST answer (everything except optional text). */
export function requiredKeys(band) {
  return questionsForBand(band).filter(q => !OPTIONAL_TYPES.has(q.type)).map(q => q.key);
}

/** Is a single answer value "present" (answered)? */
export function isAnswered(q, value) {
  if (value == null) return false;
  if (q.type === 'text' || q.type === 'textopt') return String(value).trim().length > 0;
  if (q.type === 'stars' || q.type === 'scale') return Number(value) >= 1 && Number(value) <= 5;
  return String(value).length > 0; // emoji / choice
}

/** How many required questions have an answer. */
export function answeredCount(band, answers = {}) {
  const qs = questionsForBand(band);
  return requiredKeys(band).filter(k => {
    const q = qs.find(x => x.key === k);
    return q && isAnswered(q, answers[k]);
  }).length;
}

/** Every required question answered? (optional text may be blank.) */
export function isComplete(band, answers = {}) {
  return answeredCount(band, answers) === requiredKeys(band).length;
}

/**
 * A reflection rendered for reading (coach view / player recap): one row per
 * question that has an answer, with a human label + display value.
 */
export function summarizeAnswers(band, answers = {}, lang = 'en') {
  return questionsForBand(band)
    .map(q => ({ key: q.key, label: questionLabel(q, lang), value: displayValue(q, answers[q.key], lang), type: q.type }))
    .filter(row => row.value !== '');
}

/** Turn a stored answer value into readable text. */
export function displayValue(q, value, lang = 'en') {
  if (!q || value == null || value === '') return '';
  if (q.type === 'emoji') {
    const opt = (q.options || []).find(o => o.value === value);
    return opt ? `${opt.emoji} ${optionLabel(q, value, lang)}` : '';
  }
  if (q.type === 'choice') return optionLabel(q, value, lang);
  if (q.type === 'stars' || q.type === 'scale') {
    const n = Number(value);
    return (n >= 1 && n <= 5) ? `${n}/5` : '';
  }
  return String(value).trim();
}
